/**
 * Family Selector Component
 * Displays available families and their element counts
 */

import React, { useEffect } from 'react';
import { useExtractionStore } from '@/store/extractionStore';
import { ExtractionService } from '@/services/core/extractionTools';

export function FamilySelector() {
  const {
    extractedElements,
    families,
    selectedFamily,
    extractionMode,
    setFamilies,
    setSelectedFamily,
    setExtractionMode
  } = useExtractionStore();

  // Parse families from extracted elements when they change
  useEffect(() => {
    if (extractedElements.length > 0) {
      const familyList = ExtractionService.getFamiliesFromElements(extractedElements);
      setFamilies(familyList);
      
      // Auto-select first family if available
      if (familyList.length > 0 && !selectedFamily) {
        setSelectedFamily(familyList[0].name);
      }
    } else {
      setFamilies([]);
    }
  }, [extractedElements]);

  const getTotalElements = () => {
    return families.reduce((sum, family) => sum + family.count, 0);
  };

  // Only show if we have extracted elements
  if (extractedElements.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      {/* Family Filter Section */}
      <div className="p-3 bg-dark-200 rounded-lg border border-gray-700">
        <h3 className="text-sm font-medium text-white mb-3">Filter by Family</h3>
        
        {/* Family Statistics */}
        {families.length > 0 && (
          <div className="mb-3">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-gray-500">Unique Families:</span>
                <span className="ml-2 text-white font-medium">{families.length}</span>
              </div>
              <div>
                <span className="text-gray-500">Total Elements:</span>
                <span className="ml-2 text-white font-medium">{getTotalElements()}</span>
              </div>
            </div>
          </div>
        )}

        {/* Extraction Mode Toggle */}
        <div className="flex gap-2 mb-3">
          <button
            className={`btn btn-small flex-1 ${extractionMode === 'category' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setExtractionMode('category')}
          >
            Show All Elements
          </button>
          <button
            className={`btn btn-small flex-1 ${extractionMode === 'family' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setExtractionMode('family')}
          >
            Filter by Family
          </button>
        </div>

        {/* Family List - Only show when in family mode */}
        {extractionMode === 'family' && (
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Select Family
            </label>
            
            {families.length > 0 ? (
              <div className="max-h-48 overflow-y-auto bg-dark-200 rounded-lg border border-gray-700">
                {families.map((family) => (
                  <button
                    key={family.name}
                    className={`w-full px-3 py-2 text-left hover:bg-dark-300 transition-colors flex justify-between items-center border-b border-gray-700 last:border-b-0 ${
                      selectedFamily === family.name ? 'bg-dark-300 text-primary' : 'text-gray-300'
                    }`}
                    onClick={() => setSelectedFamily(family.name)}
                  >
                    <div className="flex-1">
                      <div className="text-sm font-medium">{family.name}</div>
                      <div className="text-xs text-gray-500">
                        {family.count} element{family.count !== 1 ? 's' : ''}
                      </div>
                    </div>
                    {selectedFamily === family.name && (
                      <span className="text-primary">✓</span>
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-4 bg-dark-300 rounded-lg border border-gray-700 text-center">
                <div className="text-sm text-gray-500">
                  No families found
                </div>
              </div>
            )}

            {/* Show selected family info */}
            {selectedFamily && families.length > 0 && (
              <div className="mt-2 p-2 bg-blue-900/20 border border-blue-800 rounded text-xs">
                <span className="text-blue-400">
                  Selected: <strong>{selectedFamily}</strong> 
                  {' '}({families.find(f => f.name === selectedFamily)?.count || 0} elements)
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}